import RamblaLogo from 'assets/img/rambla-logo-black.png';
import HeroLeftIllustration from 'assets/svg/hero-left.svg';
import HeroRightIllustration from 'assets/svg/hero-right.svg';
import { CardContainer } from 'components';
import PropTypes from 'prop-types';
import React from 'react';
import { Col, Container, Row } from 'react-bootstrap';
import { MdLanguage, MdLogout } from 'react-icons/md';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { media } from 'utils/rwd';

const AuthViewport = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  height: 100dvh;
  overflow-x: hidden;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  background: #fff;
  color: #000;

  &::before {
    content: '';
    position: fixed;
    top: 150px;
    left: -260px;
    z-index: 0;
    width: 620px;
    max-width: 40vw;
    height: 500px;
    max-height: 60vh;
    background-image: url(${HeroLeftIllustration});
    background-repeat: no-repeat;
    background-position: center;
    background-size: contain;
    opacity: 0.1;
    pointer-events: none;
  }

  &::after {
    content: '';
    position: fixed;
    top: 104px;
    right: -300px;
    z-index: 0;
    width: 760px;
    max-width: 48vw;
    height: 620px;
    max-height: 68vh;
    background-image: url(${HeroRightIllustration});
    background-repeat: no-repeat;
    background-position: center;
    background-size: contain;
    opacity: 0.1;
    pointer-events: none;
  }

  ${media.md.down`
    &::before,
    &::after {
      top: 124px;
      width: 620px;
      max-width: 76vw;
      opacity: 0.1;
    }

    &::before {
      left: -320px;
      right: auto;
    }

    &::after {
      right: -240px;
    }
  `}

  ${media.sm.down`
    &::before,
    &::after {
      display: none;
    }
  `}
`;

const AuthHeader = styled.header`
  padding: 24px 56px 12px;
  position: relative;
  z-index: 1;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  ${media.sm.down`
    padding: 18px 16px 8px;
  `}
`;

const AuthLogo = styled.img`
  display: block;
  width: 164px;
  height: auto;

  ${media.xs.down`
    width: 112px;
  `}
`;

const AuthActionLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 12px 20px;
  background: #fff;
  color: #be1622;
  font-size: 14px;
  font-weight: bold;
  line-height: 1;
  text-decoration: none;
  border: 1px solid #be1622;
  border-radius: 8px;

  &:hover,
  &:focus {
    background: #be1622;
    color: #fff;
    text-decoration: none;
  }

  ${media.xs.down`
    padding: 12px 14px;
  `}
`;

const AuthActionButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 12px 20px;
  background: #fff;
  color: #be1622;
  font-size: 14px;
  font-weight: bold;
  line-height: 1;
  text-decoration: none;
  border: 1px solid #be1622;
  border-radius: 8px;
  cursor: pointer;

  &:hover,
  &:focus {
    background: #be1622;
    color: #fff;
    text-decoration: none;
  }

  ${media.xs.down`
    padding: 12px 14px;
  `}
`;

const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 10px;
  flex-wrap: wrap;
`;

const WebsiteActionLink = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 42px;
  height: 42px;
  border: 1px solid #e9ecef;
  border-radius: 50%;
  background: #fff;
  color: #000;
  text-decoration: none;

  &:hover,
  &:focus {
    border-color: #be1622;
    color: #be1622;
    text-decoration: none;
  }

  svg {
    width: 20px;
    height: 20px;
  }

  ${media.xs.down`
    width: 38px;
    height: 38px;
  `}
`;

const AuthMain = styled.main`
  position: relative;
  z-index: 1;
  display: flex;
  align-items: center;
  flex: 1 0 auto;
  width: 100%;
  padding: 12px 16px 48px;
`;

const AuthContent = styled(Container)`
  width: 100%;
`;

const AuthCard = styled.div`
  width: 100%;
  max-width: ${({ $cardMaxWidth }) => $cardMaxWidth};
  margin: 0 auto;
  padding: 48px;
  border: 1px solid rgba(0, 0, 0, 0.05);
  border-radius: 8px;
  background: #fff;
  box-shadow: 0 24px 70px rgba(0, 0, 0, 0.15);

  .auth-page-title {
    margin: 0 0 12px;
    color: #000;
    font-size: 36px;
    font-weight: bold;
    line-height: 1;
    letter-spacing: 0;
    text-align: center;
    text-transform: none;
  }

  .auth-page-subtitle {
    max-width: 460px;
    margin: 0 auto 28px;
    color: #6c757d;
    font-size: 16px;
    line-height: 1.5;
    text-align: center;
  }

  .form-label {
    color: #000;
  }

  .form-control {
    border-radius: 6px;
  }

  .auth-secondary-link {
    color: #6c757d;
    font-size: 15px;
    font-weight: bold;
    line-height: 1;
    text-align: center;
    text-decoration: none;
  }

  .auth-secondary-link:hover,
  .auth-secondary-link:focus {
    color: #000;
    text-decoration: underline;
  }

  ${media.xs.down`
    padding: 32px;

    .auth-page-title {
      font-size: 30px;
    }

    .auth-page-subtitle {
      margin-bottom: 24px;
      font-size: 15px;
    }

  `}
`;

const AuthFooterSlot = styled.div`
  position: relative;
  z-index: 1;
  flex-shrink: 0;
  width: 100%;
  padding: 0 56px 24px;

  ${media.sm.down`
    padding: 0 16px calc(20px + env(safe-area-inset-bottom));
  `}
`;

const FloatingCorner = styled.div`
  position: fixed;
  right: 20px;
  bottom: 20px;
  z-index: 1000;
`;

const renderAuthCornerAction = (cornerAction) => {
  if (!cornerAction) return null;
  if (typeof cornerAction === 'function') return cornerAction();

  const actionContent = (
    <>
      {cornerAction.icon === 'logout' && <MdLogout aria-hidden="true" focusable="false" />}
      {cornerAction.label}
    </>
  );

  if (cornerAction.to) {
    return <AuthActionLink to={cornerAction.to}>{actionContent}</AuthActionLink>;
  }

  if (cornerAction.href) {
    return (
      <AuthActionButton as="a" href={cornerAction.href}>
        {actionContent}
      </AuthActionButton>
    );
  }

  return (
    <AuthActionButton type="button" onClick={cornerAction.onClick}>
      {actionContent}
    </AuthActionButton>
  );
};

const renderHeaderActions = (cornerAction) => {
  const actions = Array.isArray(cornerAction) ? cornerAction : [cornerAction];

  return (
    <HeaderActions>
      <WebsiteActionLink
        href="https://rambla-welcome.netlify.app/"
        aria-label="Open Rambla website"
        title="Open Rambla website"
      >
        <MdLanguage aria-hidden="true" focusable="false" />
      </WebsiteActionLink>
      {actions.map((action, index) => (
        // eslint-disable-next-line react/no-array-index-key
        <React.Fragment key={index}>{renderAuthCornerAction(action)}</React.Fragment>
      ))}
    </HeaderActions>
  );
};

const FullPageCardContainer = ({
  rightCorner,
  footer,
  variant,
  cornerAction,
  cardMaxWidth,
  children,
  ...props
}) => {
  if (variant === 'auth' || variant === 'onboarding') {
    const effectiveCardMaxWidth = cardMaxWidth || (variant === 'onboarding' ? '920px' : '620px');

    return (
      <AuthViewport>
        <AuthHeader>
          <AuthLogo src={RamblaLogo} alt="Rambla" />
          {renderHeaderActions(cornerAction)}
        </AuthHeader>
        <AuthMain>
          <AuthContent>
            <Row className="justify-content-center">
              <Col {...props}>
                <AuthCard $cardMaxWidth={effectiveCardMaxWidth}>{children}</AuthCard>
              </Col>
            </Row>
          </AuthContent>
        </AuthMain>
        {footer && <AuthFooterSlot>{footer()}</AuthFooterSlot>}
        {rightCorner && <FloatingCorner>{rightCorner()}</FloatingCorner>}
      </AuthViewport>
    );
  }

  return (
    <Container
      fluid
      className="d-flex flex-column overflow-auto vh-100"
      style={{ position: 'relative' }}
    >
      <Container className="d-flex flex-column justify-content-center flex-grow-1">
        <CardContainer {...props}>{children}</CardContainer>
      </Container>
      {footer && (
        <div
          style={{
            padding: '0 0 20px 0',
            textAlign: 'center',
          }}
        >
          {footer()}
        </div>
      )}
      {rightCorner && <FloatingCorner>{rightCorner()}</FloatingCorner>}
    </Container>
  );
};

FullPageCardContainer.propTypes = {
  children: PropTypes.node,
  rightCorner: PropTypes.func,
  footer: PropTypes.func,
  variant: PropTypes.oneOf(['auth', 'onboarding']),
  cardMaxWidth: PropTypes.string,
  cornerAction: PropTypes.oneOfType([
    PropTypes.func,
    PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.func, PropTypes.object])),
    PropTypes.shape({
      label: PropTypes.node.isRequired,
      href: PropTypes.string,
      icon: PropTypes.oneOf(['logout']),
      onClick: PropTypes.func,
      to: PropTypes.string,
    }),
  ]),
};

export default FullPageCardContainer;
